<template>
    <div
        class="league"
        v-bind:class="{
            _show: isExpanded,
            JARVIS_SportPage_Btn_Collapse: isExpanded,
            JARVIS_SportPage_Btn_Expand: !isExpanded,
        }"
    >
        <div class="league-title" v-on:click="toggleExpanded">
            <div class="league-title-content">
                <img
                    v-if="isSportsCupIconEnabled && isSportsCupLeague"
                    class="sports-cup-zone-logo"
                    src="@/assets/images/euro-2024-icon-light.png"
                    alt=""
                    aria-hidden="true"
                >
                <div class="league-title-text">
                    <h2 class="league-title-name" v-bind:data-testid="`league-heading-${leagueName}`">
                        {{ leagueName }}
                    </h2>
                    <p v-if="isExpanded && leagueInformation" class="league-title-info">
                        {{ leagueInformation }}
                    </p>
                    <p v-if="isShowTwoMpSubBetInformation" class="league-title-info">
                        {{ i18n.t('mpTwoMinSubBet') }}
                    </p>
                </div>
            </div>
            <span
                v-if="!isOutright"
                class="count"
                v-bind:class="{ select: hasOddsSelect }"
            >{{ matches.length }}</span>
            <SvgIcon iconName="systemIcon_arrowBottom" />
        </div>
        <div v-if="isExpanded">
            <div v-if="isOutright" class="matchDisplay">
                <template v-for="outright in matches">
                    <LazyLoad
                        v-if="outright.type === 'outright'"
                        v-bind:key="`${outright.id}-${outright.isLive}`"
                    >
                        <Outright v-bind:info="outright" />
                    </LazyLoad>
                </template>
            </div>
            <div v-else class="matchDisplay">
                <div
                    v-for="match in matches"
                    v-bind:key="match.type === 'match' ? match.eventId : 'outright do not render this div'"
                >
                    <div v-if="isEPenaltyCard(match)">
                        <LazyLoad v-bind:placeHolderHeight="getEPenaltyPlaceholderHeight(match)">
                            <EPenaltyMatch v-if="match.type === 'match'" v-bind:match="match" />
                        </LazyLoad>
                    </div>
                    <div v-else-if="isMuayThaiEnabled(match)">
                        <LazyLoad v-bind:placeHolderHeight="18.5">
                            <MtMatch v-if="match.type === 'match'" v-bind:match="match" />
                        </LazyLoad>
                    </div>
                    <LazyLoad v-else>
                        <Match v-if="match.type === 'match'" v-bind:match="match" />
                    </LazyLoad>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import type { PropType } from 'vue';
    import LazyLoad from '@/components/common/LazyLoad.vue';
    import Match from '@/components/match/Match.vue';
    import EPenaltyMatch from '@/components/match/ePenalty/EPenaltyMatch.vue';
    import Outright from '@/components/match/marketCard/Outright.vue';
    import MtMatch from '@/components/match/muayThai/MtMatch.vue';
    import type { ILeagueMatchList } from '@/interface/ILeagueMatchList';
    import { SportType } from '@/interface/enum';
    import { useEPenaltyCard } from '@/libraries/composables/useEPenaltyCard';
    import { useLeague } from '@/libraries/composables/useLeague';
    import { i18n } from '@/libraries/i18n';
    import SvgIcon from '@/svg/SvgIcon.vue';

    const E_PENALTY_CARD_HEIGHT_WITH_STREAM_AT_393PX_WIDE = 43.5;
    const E_PENALTY_CARD_HEIGHT_WITHOUT_STREAM = 21.5;

    const props = defineProps({
        matches: {
            type: Array as PropType<ILeagueMatchList[]>,
            required: true,
        },
    });

    const {
        leagueName,
        leagueInformation,
        isOutright,
        isExpanded,
        isSportsCupLeague,
        isSportsCupIconEnabled,
        hasOddsSelect,
        isShowTwoMpSubBetInformation,
        toggleExpanded,
    } = useLeague(props.matches);

    const { isEPenaltyCard } = useEPenaltyCard();

    function isMuayThaiEnabled(match: ILeagueMatchList) {
        return match.type === 'match' && match.sportType === SportType.MuayThai;
    }

    function getEPenaltyPlaceholderHeight(match: ILeagueMatchList) {
        return match.type === 'match' && match.isLive && match.stream?.videoUrl
            ? E_PENALTY_CARD_HEIGHT_WITH_STREAM_AT_393PX_WIDE
            : E_PENALTY_CARD_HEIGHT_WITHOUT_STREAM;
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/league";
</style>
